import classNames from 'classnames';
import React, { useLayoutEffect, useRef, useState } from 'react';
import type { DistributedOmit } from 'type-fest';
import { useEffectEvent } from 'use-effect-event';
import type { ButtonProps, IconButtonProps } from '../components/Button';
import Button, { IconButton } from '../components/Button';
import EmojiPicker, { EmojiPickerPopOver } from '../components/EmojiPicker';
import Tooltip from '../components/Tooltip';
import commentEmojiIcon from '../icons/non-prefilled/16x16/commentEmoji.svg';
import ClickOutsideHandler from '../utils/ClickOutsideHandler';
import addTestId from '../utils/addTestId';
import * as styles from './MagneticWallItem.module.scss';
import type { CompactPopOverSide } from './comment/getMagneticWallItemLayout';
import useShouldShowFullPageSheet from './comment/useShouldShowFullPageSheet';
import useIsLayoutSize from './utils/useIsLayoutSize';
import { LayoutSize } from './utils/useLayoutSize';

interface ResponsivePopoverProps {
  target: React.ReactNode;
  content: React.ReactNode;
  open: boolean;
  onClose(): void;
  isPopOverMinWidthDisabled?: boolean;
  compactPopOverSide: CompactPopOverSide;
  onPopOverHeightChange: (height: number) => void;
}

const ResponsivePopover = ({
  target,
  content,
  open,
  onClose,
  isPopOverMinWidthDisabled,
  compactPopOverSide,
  onPopOverHeightChange,
}: ResponsivePopoverProps) => {
  const popOverRef = useRef<HTMLDivElement | null>(null);

  const onCompactPopOverHeightChangeEvent = useEffectEvent(
    onPopOverHeightChange,
  );

  const isShowingPopOver = open;
  const shouldShowFullPageSheet = useShouldShowFullPageSheet();

  useLayoutEffect(() => {
    const popOver = popOverRef.current;
    if (!isShowingPopOver || !popOver) return undefined;

    const getHeight = () => popOver.getBoundingClientRect().height;
    const initialHeight = getHeight();

    onCompactPopOverHeightChangeEvent(initialHeight);
    let lastHeight = initialHeight;

    const observer = new ResizeObserver(() => {
      const height = getHeight();
      if (height !== lastHeight) {
        lastHeight = height;
        onCompactPopOverHeightChangeEvent(height);
      }
    });

    observer.observe(popOver);
    return () => {
      observer.disconnect();
    };
  }, [isShowingPopOver]);

  if (!open) {
    return <>{target}</>;
  }

  if (shouldShowFullPageSheet) return null;

  return (
    <>
      <ClickOutsideHandler
        onClickOutside={onClose}
        className={classNames(styles.popOverContainer, {
          [styles.minWidthDisabled]: isPopOverMinWidthDisabled,
          [styles.sideTop]: compactPopOverSide === 'top',
        })}
      >
        <div ref={popOverRef} {...addTestId('magnetic-wall-item-pop-over')}>
          {content}
        </div>
      </ClickOutsideHandler>
      {target}
    </>
  );
};

export type MagneticWallItemSize = 'full' | 'compact';

interface RootProps {
  fullView?: React.ReactNode;
  compactView: React.ReactNode;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  onMouseDown?: React.MouseEventHandler<HTMLDivElement>;
  onTouchStart?: React.TouchEventHandler<HTMLDivElement>;
  id: string;
  focused: boolean;
  wide?: boolean;
  size: MagneticWallItemSize;
}

const Root = React.forwardRef<HTMLDivElement, RootProps>(
  (
    {
      fullView,
      compactView,
      id,
      focused,
      wide,
      onClick,
      onMouseEnter,
      onMouseLeave,
      onTouchStart,
      onMouseDown,
      size,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        {...addTestId(`magnetic-wall-item-${id}`)}
        className={styles.item}
      >
        {size === 'full' && !!fullView && (
          <div
            className={classNames(styles.fullContainer, {
              [styles.focused]: focused,
              [styles.wideContainer]: wide,
            })}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
          >
            {fullView}
          </div>
        )}
        {size === 'compact' && (
          <div
            className={classNames(styles.compactContainer, {
              [styles.focused]: focused,
            })}
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onMouseDown={onMouseDown}
            onTouchStart={onTouchStart}
            {...addTestId('magnetic-wall-item-compact-container')}
          >
            {compactView}
          </div>
        )}
      </div>
    );
  },
);

interface CardProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'className'> {
  overlapped: boolean;
  focused: boolean;
  focusable?: boolean;
  variant?: 'inset' | 'default';
}

const Card = ({
  children,
  overlapped,
  focused,
  focusable,
  variant = 'default',
  ...rest
}: CardProps) => {
  return (
    <div
      {...rest}
      className={classNames(styles.card, {
        [styles.focused]: focused,
        [styles.focusable]: focusable,
        [styles.overlapped]: overlapped,
        [styles.inset]: variant === 'inset',
      })}
    >
      {children}
    </div>
  );
};

interface WithAvatarProps {
  children: React.ReactNode;
  avatar: React.ReactNode;
  disabled?: boolean;
  innerTestId?: string;
}

const WithAvatar = ({
  children,
  avatar,
  disabled,
  innerTestId,
}: WithAvatarProps) => (
  <div
    className={classNames(styles.avatarContainer, {
      [styles.avatarContainerDisabled]: disabled,
    })}
  >
    <div className={styles.avatar}>{avatar}</div>
    <div className={styles.inner} {...addTestId(innerTestId)}>
      {children}
    </div>
  </div>
);

interface ActionContainerProps {
  children: React.ReactNode;
}

const ActionContainer = ({ children }: ActionContainerProps) => (
  <div className={styles.actionContainer}>{children}</div>
);

type MagneticWallItemIconButtonProps = DistributedOmit<
  IconButtonProps,
  'children' | 'aria-label' | 'size' | 'className'
> & {
  icon: React.ReactNode;
  tooltip: string;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  highlighted?: boolean;
};

const MagneticWallItemIconButton = React.forwardRef<
  HTMLButtonElement,
  MagneticWallItemIconButtonProps
>(
  (
    { icon, tooltip, tooltipPlacement = 'bottom', highlighted, ...props },
    ref,
  ) => (
    <Tooltip
      placement={tooltipPlacement}
      content={props.disabled ? undefined : tooltip}
      asChild
    >
      <IconButton
        {...props}
        size="md"
        className={classNames({
          [styles.highlighted]: highlighted,
        })}
        aria-label={tooltip}
        ref={ref}
      >
        {icon}
      </IconButton>
    </Tooltip>
  ),
);

type SubmitButtonProps = DistributedOmit<
  ButtonProps,
  'variant' | 'size' | 'type'
>;

const SubmitButton = ({ children, ...rest }: SubmitButtonProps) => (
  <Button type="button" variant="primary" size="small" {...rest}>
    {children}
  </Button>
);

interface SecondaryButtonProps {
  onClick(): void;
  disabled?: boolean;
  children: React.ReactNode;
}

const SecondaryButton = ({
  disabled,
  onClick,
  children,
}: SecondaryButtonProps) => (
  <Button
    disabled={disabled}
    type="button"
    variant="secondary"
    size="small"
    onClick={onClick}
  >
    {children}
  </Button>
);

interface ButtonGroupProps {
  children: React.ReactNode;
}

const ButtonGroup = ({ children }: ButtonGroupProps) => (
  <div className={styles.buttonGroup}>{children}</div>
);

interface EmojiPickerButtonProps {
  onPick: (emoji: string) => void;
  tooltipPlacement?: React.ComponentProps<typeof Tooltip>['placement'];
  disabled?: boolean;
}

const EmojiPickerButton = ({
  onPick,
  tooltipPlacement = 'bottom',
  disabled,
}: EmojiPickerButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const isSmall = useIsLayoutSize(LayoutSize.mobile);

  const handleSetIsOpen = (open: boolean) => {
    setIsOpen(!disabled && open);
  };

  return (
    <EmojiPickerPopOver
      open={isOpen}
      bounds={{ left: -6 }}
      placement={isSmall ? 'top-start' : 'left-start'}
      setOpen={handleSetIsOpen}
      target={
        <MagneticWallItemIconButton
          tooltip="Insert emoji"
          tooltipPlacement={tooltipPlacement}
          icon={commentEmojiIcon}
          onClick={() => handleSetIsOpen(true)}
          active={isOpen}
          disabled={disabled}
        />
      }
    >
      <EmojiPicker
        onPick={(emoji) => {
          handleSetIsOpen(false);
          onPick(emoji);
        }}
      />
    </EmojiPickerPopOver>
  );
};

export default {
  Root,
  EmojiPickerButton,
  ButtonGroup,
  SecondaryButton,
  SubmitButton,
  IconButton: MagneticWallItemIconButton,
  ActionContainer,
  WithAvatar,
  Card,
  ResponsivePopover,
};
